import { Fragment, Suspense, useState } from "react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { administratorRoles } from "../config/roles";
import { badgeClassName } from "../elements/badgeClassName";
import { useGlobalReducer } from "../../GlobalContext";
import GlobalDispatchMethods from "../context/globalDispatchMethods";
import { usePageState } from "../context/usePageState";
import { usePageTitle } from "../hooks/usePageTitle";
import { PageBarLayout } from "./PageBarLayout";
import { PageBarBackButton } from "./PageBarBackButton";
import { PageBarKebabMenu } from "./PageBarKebabMenu";
import { PageBarViewSelect } from "./PageBarViewSelect";
import { PageBarEditViewLink } from "./PageBarEditViewLink";
import { PageBarViewActions } from "./PageBarViewActions";
import { PageBarView } from "./PageBarViewSelect.types";
import { PageAction, PageRecord } from "./recordDefinition.types";
import { getReturnTarget } from "./returnTarget";
import { useEntityPage } from "./useEntityPage";
import { recordTitle } from "./recordTitle";

const DefaultViews: PageBarView[] = [{ key: "default", label: "Default" }];

function initialsOf(record: PageRecord, fields: string[]) {
  return fields
    .map((field) =>
      typeof record[field] === "string" ? (record[field] as string)[0] ?? "" : ""
    )
    .join("")
    .toUpperCase();
}

function labelOf(action: PageAction, record: PageRecord) {
  return typeof action.label === "function" ? action.label(record) : action.label;
}

/** The one page bar every page-builder page uses, driven by its definition. */
export function PageBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyRole } = usePageState();
  const {
    definition,
    record,
    formId,
    views,
    isNew,
    informationVisible,
    onSelectForm,
    onToggleInformation,
    onRefresh,
  } = useEntityPage();
  const globalDispatch = useGlobalReducer();
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [runningActionId, setRunningActionId] = useState<string | null>(null);
  // The page a record link was pressed on, or the record's own list at its last search.
  const returnTarget = definition?.hideNavigation
    ? null
    : getReturnTarget(definition, location.state);

  const values = record ?? {};
  const identity = definition?.identity;
  const title = recordTitle(values, identity);
  const isAdministrator = hasAnyRole(administratorRoles);
  // A page the service cannot resolve a view for offers no designer at all, whatever the role.
  const canDesignForms = isAdministrator && definition?.authorable !== false;
  // One view that nobody can change is not a choice, so there is nothing to pick from.
  const offersViews = definition?.authorable !== false;
  // Audits are the record's own history, so an unsaved record has none. The panel
  // replaces the form, and this same item is the way back to it.
  const canShowAudits = isAdministrator && !isNew;

  usePageTitle(
    title || (isNew ? identity?.newRecordTitle : identity?.fallbackTitle),
    Boolean(identity)
  );

  // An entity with no registered page has no bar to render.
  if (!definition || !identity) {
    return null;
  }

  // Actions default to a saved record only - most act on something persisted.
  const actions = (definition.actions ?? []).filter(
    (action) =>
      hasAnyRole(action.roles ?? administratorRoles) &&
      (action.visible ? action.visible(values) : !isNew)
  );
  const openAction = actions.find((action) => action.id === openActionId);
  const ActionModal = openAction?.modal;

  const startAction = async (action: PageAction) => {
    if (!action.run) {
      setOpenActionId(action.id);
      return;
    }

    setRunningActionId(action.id);
    try {
      const message = await action.run(values, globalDispatch);
      if (message) {
        globalDispatch({
          type: GlobalDispatchMethods.AddNotification,
          notification: { message, success: true },
          dispatch: globalDispatch,
        });
        onRefresh();
      }
    } finally {
      setRunningActionId(null);
    }
  };

  return (
    <>
      <PageBarLayout
        backButton={
          returnTarget && (
            <PageBarBackButton
              title={returnTarget.title}
              onClick={() => navigate(returnTarget.path)}
            />
          )
        }
        identity={
          <>
            {!isNew && identity.showAvatar && (
              <span
                id="header-bar-avatar"
                className="d-none d-md-flex align-items-center justify-content-center"
              >
                {initialsOf(
                  values,
                  identity.avatarFields ?? identity.titleFields
                )}
              </span>
            )}
            <div>
              <h1 className="h4 fw-bold mb-0 text-truncate">
                {title ||
                  (isNew ? identity.newRecordTitle : identity.fallbackTitle)}
              </h1>
              {(identity.subtitleField || identity.subtitle) && (
                <span
                  id="header-bar-meta"
                  className="d-flex flex-wrap align-items-center gap-2"
                >
                  {identity.subtitle
                    ? identity.subtitle(values)
                    : typeof values[identity.subtitleField!] === "string"
                    ? (values[identity.subtitleField!] as string)
                    : ""}
                </span>
              )}
            </div>
          </>
        }
        badges={
          !isNew &&
          definition.badges && (
            <>
              {definition.badges
                .filter((badge) => badge.visible?.(values) ?? true)
                .map((badge) => (
                <span
                  key={badge.id}
                  className={classNames(
                    badgeClassName(badge.variant(values)),
                    "header-bar-badge d-flex align-items-center gap-1"
                  )}
                >
                  {badge.icon && <FontAwesomeIcon icon={badge.icon(values)} />}
                  {badge.label(values)}
                </span>
              ))}
            </>
          )
        }
        actions={
          <>
            {offersViews && (
              <PageBarViewSelect
                // The views the service said this record and user qualify for, merged
                // with the entity's code-defined ones.
                views={views.length > 0 ? views : DefaultViews}
                selectedKey={formId}
                onSelect={onSelectForm}
              />
            )}
            {canDesignForms && (
              <PageBarEditViewLink pagePath={definition.path} formId={formId} />
            )}
          </>
        }
        menu={
          // Two permissions in one menu, and either alone is reason for it to exist.
          (canDesignForms ||
            canShowAudits ||
            actions.length > 0) && (
            <PageBarKebabMenu>
              {(close) => {
                // Three groups, any of which may be absent. A rule goes between whichever
                // are present rather than being written between named pairs, so Audits is
                // separated from what precedes it however the permissions fall - it used
                // to run straight on from the record actions with nothing between them.
                const groups = [
                  canDesignForms && (
                    <PageBarViewActions
                      pagePath={definition.path}
                      formId={formId}
                      onNavigate={close}
                    />
                  ),
                  actions.length > 0 && (
                    <>
                      {actions.map((action) => (
                        <button
                          key={action.id}
                          className="header-bar-menu-item"
                          // One run at a time, so a second press can't send twice.
                          disabled={runningActionId !== null}
                          onClick={() => {
                            close();
                            startAction(action);
                          }}
                        >
                          {labelOf(action, values)}
                        </button>
                      ))}
                    </>
                  ),
                  canShowAudits && (
                    <button
                      className="header-bar-menu-item"
                      onClick={() => {
                        close();
                        onToggleInformation();
                      }}
                    >
                      {informationVisible ? "Back to form" : "Audits"}
                    </button>
                  ),
                ].filter(Boolean);

                return groups.map((group, index) => (
                  <Fragment key={index}>
                    {index > 0 && <div className="header-bar-menu-divider" />}
                    {group}
                  </Fragment>
                ));
              }}
            </PageBarKebabMenu>
          )
        }
      />
      {ActionModal && (
        // Modals load on demand, so nothing renders while one is fetched.
        <Suspense fallback={null}>
          <ActionModal
            record={values}
            onClose={() => setOpenActionId(null)}
            onDone={onRefresh}
          />
        </Suspense>
      )}
    </>
  );
}

export default PageBar;
